import { Box, styled } from "@mui/material";
import { ArrowRight, Check } from "lucide-react";

const HeroSection = styled(Box)({
  position: "relative",
  minHeight: "100vh",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  paddingTop: "80px",
  paddingBottom: "96px",
  paddingLeft: "16px",
  paddingRight: "16px",
  overflow: "hidden",
  "@media (min-width: 640px)": {
    paddingLeft: "24px",
    paddingRight: "24px",
  }
});

const ContentContainer = styled(Box)({
  maxWidth: "1536px",
  margin: "0 auto",
  textAlign: "center",
  position: "relative",
  zIndex: 10
});

const ContentInner = styled(Box)({
  maxWidth: "1024px",
  margin: "0 auto"
});

const LabelBadge = styled(Box)({
  display: "inline-flex",
  alignItems: "center",
  gap: "8px",
  padding: "8px 16px",
  background: "rgba(255, 255, 255, 0.05)",
  border: "1px solid rgba(255, 255, 255, 0.1)",
  borderRadius: "9999px",
  marginBottom: "24px",
  backdropFilter: "blur(4px)"
});

const PulsingDot = styled(Box)({
  width: "8px",
  height: "8px",
  background: "#FF6B35",
  borderRadius: "50%",
  animation: "pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite",
  "@keyframes pulse": {
    "0%, 100%": {
      opacity: 1,
    },
    "50%": {
      opacity: 0.5,
    }
  }
});

const LabelText = styled("span")({
  color: "rgba(255, 255, 255, 0.8)",
  textTransform: "uppercase",
  letterSpacing: "0.15em",
  fontFamily: "'Oswald', sans-serif",
  fontWeight: 500,
  fontSize: "0.75rem"
});

const Headline = styled("h1")({
  color: "#ffffff",
  marginBottom: "32px",
  textTransform: "uppercase",
  fontFamily: "'Oswald', 'Impact', 'Arial Black', sans-serif",
  fontWeight: 700,
  fontSize: "clamp(2.5rem, 8vw, 6rem)",
  lineHeight: 0.9,
  letterSpacing: "-0.05em",
  textShadow: "0 4px 20px rgba(0,0,0,0.8)",
  filter: "drop-shadow(0 25px 25px rgba(0, 0, 0, 0.15))"
});

const OrangeText = styled("span")({
  color: "#FF6B35"
});

const Subheadline = styled("p")({
  color: "rgba(255, 255, 255, 0.9)",
  marginBottom: "48px",
  maxWidth: "672px",
  margin: "0 auto 48px",
  fontFamily: "'Oswald', sans-serif",
  fontWeight: 300,
  fontSize: "clamp(1.125rem, 2vw, 1.5rem)",
  lineHeight: 1.6,
  textShadow: "0 2px 10px rgba(0,0,0,0.8)",
  filter: "drop-shadow(0 10px 8px rgba(0, 0, 0, 0.04))"
});

const BoldSubtext = styled("span")({
  color: "#ffffff",
  fontWeight: 600
});

const CTAButton = styled("button")({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "12px",
  margin: "0 auto 48px",
  padding: "20px 48px",
  background: "#FF6B35",
  color: "#ffffff",
  borderRadius: "12px",
  border: "none",
  cursor: "pointer",
  transition: "all 0.3s ease",
  textTransform: "uppercase",
  fontFamily: "'Oswald', sans-serif",
  fontWeight: 600,
  fontSize: "1rem",
  letterSpacing: "0.1em",
  boxShadow: "0 25px 50px -12px rgba(255, 107, 53, 0.3)",
  "&:hover": {
    background: "#E85A2A",
    transform: "scale(1.03)"
  },
  "&:active": {
    transform: "scale(0.97)"
  }
});

const TrustIndicators = styled(Box)({
  display: "flex",
  flexWrap: "wrap",
  alignItems: "center",
  justifyContent: "center",
  gap: "32px 32px",
  marginBottom: "48px",
  fontFamily: "'Oswald', sans-serif",
  fontWeight: 400,
  fontSize: "0.875rem",
  color: "rgba(255, 255, 255, 0.6)",
  "@media (max-width: 640px)": {
    gap: "12px 24px"
  }
});

const TrustItem = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "8px"
});

const StatsSection = styled(Box)({
  display: "grid",
  gridTemplateColumns: "repeat(3, 1fr)",
  gap: "24px",
  maxWidth: "672px",
  margin: "0 auto",
  paddingTop: "48px",
  borderTop: "1px solid rgba(255, 255, 255, 0.1)"
});

const StatItem = styled(Box)({
  textAlign: "center"
});

const StatNumber = styled("div")({
  color: "#ffffff",
  lineHeight: 1,
  marginBottom: "8px",
  fontFamily: "'Oswald', sans-serif",
  fontWeight: 700,
  fontSize: "clamp(2.5rem, 6vw, 4rem)",
  letterSpacing: "-0.03em"
});

const StatLabel = styled("div")({
  color: "rgba(255, 255, 255, 0.4)",
  textTransform: "uppercase",
  fontFamily: "'Oswald', sans-serif",
  fontWeight: 500,
  fontSize: "0.65rem",
  letterSpacing: "0.1em",
  lineHeight: 1.4
});

function NewHero({ onCTAClick }) {
  const handleCTAClick = () => {
    // Track hero CTA click
    if (window.gtag) {
      window.gtag("event", "hero_cta_click", {
        button_location: "hero",
        button_text: "GET YOUR PLAN NOW"
      });
    }
    onCTAClick();
  };

  return (
    <HeroSection>
      <ContentContainer>
        <ContentInner>
          <Box sx={{ mb: 4 }}>
            {/* Label Badge */}
            <LabelBadge>
              <PulsingDot />
              <LabelText>AI-Powered Transformation</LabelText>
            </LabelBadge>

            {/* MASSIVE Headline */}
            <Headline>
              Stop Guessing.
              <br />
              Get Your <OrangeText>Science-Based</OrangeText>
              <br />
              Calisthenics Plan.
            </Headline>

            {/* Subheadline */}
            <Subheadline>
              Forget generic apps. Get a personalized bodyweight progression tailored to your equipment (Floor, Bar, Rings) and skill level.{" "}
              <BoldSubtext>Generated in 10 seconds.</BoldSubtext>
            </Subheadline>

            {/* CTA Button */}
            <CTAButton onClick={handleCTAClick}>
              <span>GET YOUR PLAN NOW</span>
              <ArrowRight size={20} strokeWidth={3} />
            </CTAButton>

            {/* Trust Indicators */}
            <TrustIndicators>
              <TrustItem>
                <Check size={16} color="#F59E0B" strokeWidth={3} />
                <span>🔒 4-Week Program</span>
              </TrustItem>

              <TrustItem>
                <Check size={16} color="#F59E0B" strokeWidth={3} />
                <span>One-Time Payment</span>
              </TrustItem>

              <TrustItem>
                <Check size={16} color="#F59E0B" strokeWidth={3} />
                <span>Lifetime Access</span>
              </TrustItem>
            </TrustIndicators>

            {/* Stats Section */}
            <StatsSection>
              <StatItem>
                <StatNumber>&lt;10s</StatNumber>
                <StatLabel>Plan Ready</StatLabel>
              </StatItem>
              <StatItem>
                <StatNumber>$9.99</StatNumber>
                <StatLabel>
                  One-Time
                  <br />
                  Founding Price
                </StatLabel>
              </StatItem>
              <StatItem>
                <StatNumber>∞</StatNumber>
                <StatLabel>Unique Plans</StatLabel>
              </StatItem>
            </StatsSection>
          </Box>
        </ContentInner>
      </ContentContainer>
    </HeroSection>
  );
}

export default NewHero;
